/**
 * SupplyItemPickerModal — aranabilir MUTFAK-DIŞI kalem (SupplyItem) seçici.
 *
 * Temizlik malzemesi, ambalaj, sarf gibi kalemler buradan seçilir. Bu kalemler
 * stok/menü/POS'tan tamamen izoledir (backend SupplyItem modeli) — sadece satın
 * alma (PO/GR/RFQ) akışında "alınabilir kalem" olarak yer alır.
 *
 * Desen: IngredientPickerModal ile aynı (server-side debounced arama +
 * infinite-scroll). Yeni kalem oluşturma BURADA YOK — kalem tanımlama/kategoriye
 * atama ana menüdeki "Tedarik Kategorileri" / stok ekranlarından yapılır.
 *
 * `excludeIds` ile forma zaten eklenmiş kalemler gizlenir.
 */
import { useState } from "react";
import {
  Badge, Box, Button, Group, Loader, Modal, ScrollArea, Stack,
  Text, TextInput, UnstyledButton,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useDebouncedValue } from "@mantine/hooks";
import { useInfiniteResource } from "../../../shared/api";
import { MoneyDisplay, useInfiniteScrollRef } from "../../../shared/ui";

export interface SupplyItem {
  id: string;
  name: string;
  sku: string;
  unit: string;
  category: string | null;
  category_name: string | null;
  last_price_minor: number;
  is_active: boolean;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Seçilen kalem — modal kapanır. */
  onSelect: (item: SupplyItem) => void;
  /** Listeden gizlenecek id'ler (forma zaten eklenenler). */
  excludeIds?: string[];
}

export function SupplyItemPickerModal({ opened, onClose, onSelect, excludeIds = [] }: Props) {
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search.trim(), 300);

  const {
    items, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useInfiniteResource<SupplyItem>("supply-items", { search: debounced });

  const excluded = new Set(excludeIds);
  const visible = items.filter((i) => !excluded.has(i.id));

  const sentinelRef = useInfiniteScrollRef<HTMLDivElement>({
    hasNextPage, isFetchingNextPage, fetchNextPage, itemCount: items.length,
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Mutfak-dışı Kalem Seç" size="md">
      <Stack gap="sm">
        <Text size="xs" c="dimmed">
          Temizlik / ambalaj / sarf kalemi arayın ve seçin (stok takibi yapılmaz)
        </Text>
        <TextInput
          placeholder="Kalem adı veya SKU ile ara..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          autoFocus
        />

        <ScrollArea.Autosize mah={420} offsetScrollbars>
          {isLoading ? (
            <Group justify="center" py="xl"><Loader /></Group>
          ) : visible.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl" size="sm">
              {debounced ? "Eşleşen kalem yok" : "Henüz mutfak-dışı kalem yok"}
            </Text>
          ) : (
            <Stack gap={6}>
              {visible.map((i) => (
                <UnstyledButton
                  key={i.id}
                  onClick={() => { onSelect(i); onClose(); }}
                  style={{
                    border: "1px solid var(--mantine-color-gray-3)",
                    borderRadius: "var(--mantine-radius-md)",
                    padding: 10,
                  }}
                >
                  <Group gap="sm" wrap="nowrap" justify="space-between">
                    <Box style={{ minWidth: 0 }}>
                      <Text fw={600} size="sm" truncate>{i.name}</Text>
                      <Group gap={6}>
                        <Text size="xs" c="dimmed" truncate>
                          SKU: {i.sku || "-"} · Birim: {i.unit}
                        </Text>
                        {i.category_name && (
                          <Badge color="teal" variant="light" size="xs">
                            {i.category_name}
                          </Badge>
                        )}
                      </Group>
                    </Box>
                    <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                      {i.last_price_minor > 0 && (
                        <Text size="sm" c="dimmed">
                          <MoneyDisplay minor={i.last_price_minor} />
                          <Text span size="xs"> / {i.unit}</Text>
                        </Text>
                      )}
                      {!i.is_active && <Badge color="gray" variant="light" size="sm">pasif</Badge>}
                    </Group>
                  </Group>
                </UnstyledButton>
              ))}
              {hasNextPage && (
                <Box ref={sentinelRef}>
                  <Group justify="center" py="sm"><Loader size="xs" /></Group>
                </Box>
              )}
            </Stack>
          )}
        </ScrollArea.Autosize>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
